import * as React from 'react';
import { Subtract } from 'utility-types';
import { validate as uuidValidate } from 'uuid';

import { Filter } from '~commonInfra/utils/Filter';
import { IPredictiveModelDetailInfo, IPredictiveModelInfo } from '~src/predictiveModel/models/IPredictiveModelInfo';
import { PredictiveModels } from '~src/predictiveModel/resources/PredictiveModels';
import { IPredictiveModelActions } from '~src/predictiveModel/serverApi/types/IPredictiveModelActions';
import { Projects } from '~src/projects/resources/Projects';
import { RequestAccessError } from '~src/requestAccess/components/RequestAccessError';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { INamedItemInfo } from '~src/spacesPlatform/models/resourceInfoParts';

import { RouterError } from '../../bootstrap/routerHelpers/RouterError';

interface InjectedPredictiveModelSpaceData {
  readonly predictiveModelAllowedActions: IPredictiveModelActions;
  readonly predictiveModelDetailInfo: IPredictiveModelDetailInfo;
  readonly predictiveModelInfo: IPredictiveModelInfo;
  readonly projectHierarchy: INamedItemInfo[];
}

export interface InjectedPredictiveModelSpaceDataFetcherProps extends InjectedPredictiveModelSpaceData {
  readonly refreshData: (predictiveModelIdOrLuid?: string) => void;
}

export interface PredictiveModelSpaceDataFetcherProps {
  readonly siteName: string;
  readonly predictiveModelIdOrLuid: string;
  readonly siteUrls: SpacesSiteUrls;
  readonly isRequestAccessEnabled: boolean;
}

export function withPredictiveModelSpaceDataFetcher<P extends InjectedPredictiveModelSpaceDataFetcherProps>(
  Component: React.ComponentType<P>
) {
  // TODO - achristian - TFSID:1059566 - 2020-02-03 - Redo this component with less side-effects.
  return function PredictiveModelSpaceDataFetcher(
    props: Subtract<P, InjectedPredictiveModelSpaceDataFetcherProps> & PredictiveModelSpaceDataFetcherProps
  ) {
    const [ error, setError ] = React.useState(false);
    const [ injectedData, setInjectedData ] = React.useState<InjectedPredictiveModelSpaceData>(undefined);
    const [ errorCode, setErrorCode ] = React.useState(null);

    let didCancel = false;

    const refreshData = (predictiveModelIdOrLuid?: string) => {
      fetchPredictiveModelSpaceData(predictiveModelIdOrLuid ?? props.predictiveModelIdOrLuid)
        .then(injectedData => {
          !didCancel && setError(false);
          !didCancel && setInjectedData(injectedData);
        })
        .catch((e) => {
          !didCancel && setError(true);
          !didCancel && setErrorCode(e);
        });
    };

    React.useEffect(() => {
      refreshData();
      // This noncompliance existed before we enabled the lint rule. If you’re here, please consider fixing it!
      // eslint-disable-next-line react-hooks/exhaustive-deps
      return () => didCancel = true;
    }, [ props.predictiveModelIdOrLuid, props.siteName ]);

    if (error) {
      if (props.isRequestAccessEnabled) {
        return (
          <RequestAccessError
            url={props.siteUrls.exploreUrl()}
            contentIdentifier={props.predictiveModelIdOrLuid}
            contentType={'predictiveModel'}
            errorCode={errorCode}
          />
        );
      }
      return <RouterError errorType={'predictiveModel'} urls={props.siteUrls}/>;
    } else if (!injectedData) {
      return null;
    }

    return <Component {...props as unknown as P} {...injectedData} refreshData={refreshData}/>;
  };
}

// The viewer fetches by id, whereas the editor fetches by luid
async function fetchPredictiveModelSpaceData(predictiveModelIdOrLuid: string): Promise<InjectedPredictiveModelSpaceData> {
  if (!predictiveModelIdOrLuid) {
    const emptySpaceData: InjectedPredictiveModelSpaceData = {
      predictiveModelAllowedActions: undefined,
      predictiveModelDetailInfo: undefined,
      predictiveModelInfo: undefined,
      projectHierarchy: []
    };
    return emptySpaceData;
  }
  const isLuid: boolean = uuidValidate(predictiveModelIdOrLuid);
  let predictiveModelDetailInfo: IPredictiveModelDetailInfo = isLuid ? await PredictiveModels.getByLuid(predictiveModelIdOrLuid) : await PredictiveModels.getById(predictiveModelIdOrLuid);
  const predictiveModelId = predictiveModelDetailInfo.id;
  if (isLuid) {
    predictiveModelDetailInfo = await PredictiveModels.getById(predictiveModelId);
  }
  const predictiveModelAllowedActions: IPredictiveModelActions = await PredictiveModels.actions(predictiveModelId);
  const predictiveModelInfo: IPredictiveModelInfo = await PredictiveModels.all().withFilters([Filter.fieldEq('id', predictiveModelId)]).getFirst();
  let projectHierarchy: INamedItemInfo[] = [];

  if (predictiveModelDetailInfo.container?.id) {
    projectHierarchy = await Projects.getProjectHierarchy(predictiveModelDetailInfo.container.id);
  } else if (predictiveModelDetailInfo.container?.luid) {
    projectHierarchy = await Projects.getProjectHierarchyByLuid(predictiveModelDetailInfo.container.luid);
  }

  return {
    predictiveModelAllowedActions,
    predictiveModelDetailInfo,
    predictiveModelInfo,
    projectHierarchy,
  };
}
